import React, { useState, useEffect, useRef } from 'react';
import { TagBadge } from './TagBadge';
import { getTags, createTag } from '../../services/campaignTags';
import type { CampaignTag } from '../../types/campaign';
import toast from 'react-hot-toast';

interface TagSelectorProps {
  selectedTags: CampaignTag[];
  onChange: (tags: CampaignTag[]) => void;
  disabled?: boolean;
}

const PRESET_COLORS = [
  '#6366f1', // Indigo
  '#8b5cf6', // Purple
  '#ec4899', // Pink
  '#ef4444', // Red
  '#f97316', // Orange
  '#eab308', // Yellow
  '#22c55e', // Green
  '#06b6d4', // Cyan
  '#3b82f6', // Blue
  '#64748b', // Slate
];

export const TagSelector: React.FC<TagSelectorProps> = ({
  selectedTags,
  onChange,
  disabled = false,
}) => {
  const [availableTags, setAvailableTags] = useState<CampaignTag[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState(PRESET_COLORS[0]);
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadTags();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
        setIsCreatingNew(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadTags = async () => {
    try {
      const tags = await getTags();
      setAvailableTags(tags);
    } catch (error) {
      console.error('Failed to load tags:', error);
      toast.error('Failed to load tags');
    }
  };

  const handleAddTag = (tag: CampaignTag) => {
    if (!selectedTags.find((t) => t.id === tag.id)) {
      onChange([...selectedTags, tag]);
    }
    setIsDropdownOpen(false);
  };

  const handleRemoveTag = (tagId: number) => {
    onChange(selectedTags.filter((t) => t.id !== tagId));
  };

  const handleCreateTag = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newTagName.trim()) {
      toast.error('Tag name is required');
      return;
    }

    setIsLoading(true);
    try {
      const newTag = await createTag({ name: newTagName.trim(), color: newTagColor });
      setAvailableTags([...availableTags, newTag]);
      onChange([...selectedTags, newTag]);
      setNewTagName('');
      setNewTagColor(PRESET_COLORS[0]);
      setIsCreatingNew(false);
      toast.success('Tag created successfully');
    } catch (error: any) {
      console.error('Failed to create tag:', error);
      const errorMessage = error.response?.data?.message || 'Failed to create tag';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const unselectedTags = availableTags.filter(
    (tag) => !selectedTags.find((t) => t.id === tag.id)
  );

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">Tags</label>

      {/* Selected tags */}
      <div className="flex flex-wrap gap-2 min-h-[2rem]">
        {selectedTags.length > 0 ? (
          selectedTags.map((tag) => (
            <div key={tag.id} className="inline-flex items-center">
              <TagBadge tag={tag} />
              <button
                type="button"
                onClick={() => handleRemoveTag(tag.id)}
                disabled={disabled}
                className="ml-1 text-gray-400 hover:text-gray-600 focus:outline-none disabled:opacity-50"
                aria-label="Remove tag"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          ))
        ) : (
          <span className="text-sm text-gray-400">No tags selected</span>
        )}
      </div>

      {/* Add tag button/dropdown */}
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          disabled={disabled}
          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-md hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg
            className="w-4 h-4 mr-1"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4v16m8-8H4"
            />
          </svg>
          Add Tag
        </button>

        {/* Dropdown */}
        {isDropdownOpen && (
          <div className="absolute z-10 mt-1 w-64 bg-white border border-gray-200 rounded-md shadow-lg">
            <div className="p-2 max-h-60 overflow-y-auto">
              {unselectedTags.length > 0 ? (
                <div className="space-y-1">
                  {unselectedTags.map((tag) => (
                    <button
                      key={tag.id}
                      type="button"
                      onClick={() => handleAddTag(tag)}
                      className="w-full text-left px-2 py-1.5 rounded hover:bg-gray-50 focus:outline-none focus:bg-gray-50"
                    >
                      <TagBadge tag={tag} />
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 px-2 py-1">
                  {availableTags.length === 0
                    ? 'No tags available'
                    : 'All tags already selected'}
                </p>
              )}

              {/* Create new tag section */}
              {!isCreatingNew ? (
                <button
                  type="button"
                  onClick={() => setIsCreatingNew(true)}
                  className="w-full mt-2 px-2 py-1.5 text-sm text-indigo-600 hover:bg-indigo-50 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 text-left"
                >
                  + Create new tag
                </button>
              ) : (
                <form onSubmit={handleCreateTag} className="mt-2 p-2 border-t border-gray-200">
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={newTagName}
                      onChange={(e) => setNewTagName(e.target.value)}
                      placeholder="Tag name"
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      disabled={isLoading}
                      autoFocus
                    />
                    <div className="flex gap-1">
                      {PRESET_COLORS.map((color) => (
                        <button
                          key={color}
                          type="button"
                          onClick={() => setNewTagColor(color)}
                          className={`w-6 h-6 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-400 ${
                            newTagColor === color ? 'ring-2 ring-offset-1 ring-gray-600' : ''
                          }`}
                          style={{ backgroundColor: color }}
                          disabled={isLoading}
                          aria-label={`Select color ${color}`}
                        />
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="submit"
                        disabled={isLoading || !newTagName.trim()}
                        className="flex-1 px-2 py-1 text-xs font-medium text-white bg-indigo-600 rounded hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isLoading ? 'Creating...' : 'Create'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setIsCreatingNew(false);
                          setNewTagName('');
                          setNewTagColor(PRESET_COLORS[0]);
                        }}
                        disabled={isLoading}
                        className="px-2 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

TagSelector.displayName = 'TagSelector';
